import { Box, Group, Text } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { HubTable } from '@/components/Hub/HubTable';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { alert } from '@/services/alert';
import { fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { ORBIT_DISABLED, ORBIT_NEEDS_ACTION } from '@/theme/colors';
import { LedgerCell, LedgerEmptyCell } from '../ledger/LedgerCell';
import { formatLedgerMoney, LedgerRow, sourceLabel } from '../ledger/ledgerRows';
import { approvedMessage, formatRequestDate, issuedMessage, requestType } from './refundRequests';
import { ApprovePopover, LinkRefundPopover } from './RefundPopovers';
import { RefundRequestRow, RefundWorklistsState } from './useRefundWorklists';

/**
 * Revenue → Refunds (BAR-3033): requests with no refund yet, and refunds with
 * no request. PNs and Care Managers raise requests; Finance approves and links
 * them (BAR-3061). A clean month has both lists empty.
 */

const BOT_NAME = 'create-treatment-payment';
const MONEY = { align: 'right' as const };
const SECTION_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const DOT_STYLE: CSSProperties = { width: 7, height: 7, borderRadius: 999, background: ORBIT_NEEDS_ACTION };

const message = (error: unknown): string => (error instanceof Error ? error.message : 'unknown error');

function SectionHeading({ children }: { children: string }): ReactElement {
  return (
    <Group gap={7} px={16} pt={12} pb={8}>
      <span style={DOT_STYLE} />
      <Text fz={13} fw={600}>
        {children}
      </Text>
    </Group>
  );
}

export function RefundsView({ worklists }: { worklists: RefundWorklistsState }): ReactElement {
  const medplum = useMedplum();
  const { requests, refunds, loading, error, reload } = worklists;

  useEffect(() => {
    if (error) alert.error(`Couldn't load refunds: ${error}`);
  }, [error]);

  const requestColumns = useMemo((): MRT_ColumnDef<RefundRequestRow>[] => {
    // The bot restages the request's patient, whose payment status it moves (BAR-3082).
    const patientOf = (request: RefundRequestRow): Reference<Patient> | undefined =>
      request.medplumPatientId
        ? { reference: getReferenceString({ resourceType: 'Patient', id: request.medplumPatientId }) }
        : undefined;

    const undo = (operation: string, request: RefundRequestRow) => () =>
      fetchBotWrite(medplum, BOT_NAME, { operation, requestId: request.id, patient: patientOf(request) }, reload)
        .then(reload)
        .catch((err: unknown) => alert.error(`Couldn't undo: ${message(err)}`));

    const approve = (request: RefundRequestRow, amount: number, note: string): void => {
      fetchBotWrite<{ approval: { linked: boolean } }>(
        medplum,
        BOT_NAME,
        {
          operation: 'approveRefundRequest',
          requestId: request.id,
          patient: patientOf(request),
          amount,
          note: note || null,
        },
        reload
      )
        .then(({ approval }) => {
          reload();
          showUndoToast(
            approvedMessage(request, request.patientDisplayName, amount, approval.linked),
            undo('unapproveRefundRequest', request)
          );
        })
        .catch((err: unknown) => alert.error(`Couldn't approve: ${message(err)}`));
    };

    const link = (request: RefundRequestRow, refund: LedgerRow): void => {
      fetchBotWrite(
        medplum,
        BOT_NAME,
        {
          operation: 'linkRefundRequest',
          requestId: request.id,
          patient: patientOf(request),
          record: refund.record,
          entryId: refund.id,
        },
        reload
      )
        .then(() => {
          reload();
          showUndoToast(
            issuedMessage(request, request.patientDisplayName, refund),
            undo('unlinkRefundRequest', request)
          );
        })
        .catch((err: unknown) => alert.error(`Couldn't link the refund: ${message(err)}`));
    };

    return [
      {
        id: 'patient',
        header: 'Patient',
        size: 140,
        grow: 1,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell>{row.original?.patientDisplayName}</LedgerCell>,
      },
      {
        id: 'requested',
        header: 'Requested',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <LedgerCell>{row.original?.requestedAt ? formatRequestDate(row.original.requestedAt) : ''}</LedgerCell>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell>{row.original ? requestType(row.original) : ''}</LedgerCell>,
      },
      {
        id: 'reason',
        header: 'Reason',
        size: 150,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell title={row.original?.reason}>{row.original?.reason}</LedgerCell>,
      },
      {
        id: 'note',
        header: 'Note',
        size: 180,
        grow: 1.4,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.note ? (
            <LedgerCell title={row.original.note}>{row.original.note}</LedgerCell>
          ) : (
            <LedgerEmptyCell />
          ),
      },
      {
        id: 'amountRequested',
        header: 'Requested',
        size: 100,
        enableSorting: false,
        mantineTableHeadCellProps: MONEY,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) => (
          <LedgerCell style={{ textAlign: 'right' }}>
            {row.original ? formatLedgerMoney(-row.original.amountRequested) : ''}
          </LedgerCell>
        ),
      },
      {
        id: 'amountApproved',
        header: 'Approved',
        size: 100,
        enableSorting: false,
        mantineTableHeadCellProps: MONEY,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) =>
          row.original?.amountApproved !== null && row.original?.amountApproved !== undefined ? (
            <LedgerCell style={{ textAlign: 'right' }} title={row.original.approvalNote ?? ''}>
              {formatLedgerMoney(-row.original.amountApproved)}
            </LedgerCell>
          ) : (
            <LedgerCell color={ORBIT_DISABLED} style={{ textAlign: 'right' }}>
              —
            </LedgerCell>
          ),
      },
      {
        id: 'action',
        header: '',
        size: 100,
        enableSorting: false,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) => {
          const request = row.original;
          if (!request?.id) return null;
          return request.amountApproved === null ? (
            <ApprovePopover request={request} onApprove={(amount, note) => approve(request, amount, note)} />
          ) : (
            <LinkRefundPopover request={request} refunds={refunds} onLink={(refund) => link(request, refund)} />
          );
        },
      },
    ];
  }, [medplum, refunds, reload]);

  const refundColumns = useMemo(
    (): MRT_ColumnDef<LedgerRow>[] => [
      {
        id: 'patient',
        header: 'Patient',
        size: 150,
        grow: 1,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.patientDisplayName ? (
            <LedgerCell>{row.original.patientDisplayName}</LedgerCell>
          ) : (
            <LedgerCell color={ORBIT_DISABLED}>{row.original?.payerName ?? ''}</LedgerCell>
          ),
      },
      {
        id: 'refunded',
        header: 'Refunded',
        size: 104,
        enableSorting: false,
        Cell: ({ row }) => (
          <LedgerCell>{row.original?.occurredAt ? formatRequestDate(row.original.occurredAt) : ''}</LedgerCell>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        size: 104,
        enableSorting: false,
        mantineTableHeadCellProps: MONEY,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) => (
          <LedgerCell style={{ textAlign: 'right' }}>
            {row.original?.id ? formatLedgerMoney(row.original.amount) : ''}
          </LedgerCell>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        size: 104,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell>{sourceLabel(row.original?.source ?? null)}</LedgerCell>,
      },
      {
        id: 'transactionId',
        header: 'Transaction ID',
        size: 160,
        enableSorting: false,
        Cell: ({ row }) => (
          <LedgerCell title={row.original?.transactionId ?? ''}>{row.original?.transactionId}</LedgerCell>
        ),
      },
    ],
    []
  );

  return (
    <Box style={SECTION_STYLE}>
      <Box style={SECTION_STYLE}>
        <SectionHeading>Requested, no refund yet</SectionHeading>
        <HubTable
          columns={requestColumns}
          data={requests}
          loading={loading}
          emptyText="None"
          emptyFallback={error && <LoadFailed what="refund requests" error={error} onRetry={reload} />}
        />
      </Box>
      <Box style={{ ...SECTION_STYLE, borderTop: '1px solid rgba(0, 0, 0, 0.10)' }}>
        <SectionHeading>Refunded, no request</SectionHeading>
        <HubTable
          columns={refundColumns}
          data={refunds}
          loading={loading}
          emptyText="None"
          emptyFallback={error && <LoadFailed what="refunds" error={error} onRetry={reload} />}
        />
      </Box>
    </Box>
  );
}
